import { useEffect, memo, useState, useCallback } from 'react'
import { observer } from 'mobx-react'
import { useInView } from 'react-intersection-observer'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Grid, { GridSize } from '@mui/material/Grid'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Theme, useTheme } from '@mui/material/styles'

import createStyles from '@mui/styles/createStyles'
import withStyles from '@mui/styles/withStyles'

import SaveButton from 'components/SaveButton/SaveButton'
import SaveButtonDialog, {
  SaveButtonDialogState
} from 'components/SaveButton/SaveButtonDialog'
import { LoadingMode } from 'components/LoadingProvider'
import HotlineFallback from 'components/HotlineFallback'
import { useErrorState } from 'context/Error/hook'
import Wave from 'components/Wave'
import CategoryLayout from 'components/Layout/Category'
import PreviousPageErrors from 'components/PageErrors/PreviousPageErrors'
import { useSaveButton } from 'hooks/useSaveButton'
import { useRootStore } from 'stores/utils/hooks/useRootStore'
import { useStores } from 'stores/utils/hooks/useStores'
import { useLoadingMode } from 'stores/utils/hooks/useLoadingMode'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { useDebouncedEffect } from 'hooks/useDebouncedEffect'
import { Button } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useAsyncEffect } from 'hooks/useAsyncEffect'
import LegalHint from 'components/LegalHint'
import { NavigateFunction } from 'react-router-dom'

type Props = {
  onBack?: () => void
  onNext: () => void
  legalContact: string
  navigate: NavigateFunction
}

const FlexBasisAutoGrid = withStyles(() =>
  createStyles({
    root: {
      flexBasis: 'auto'
    }
  })
)(Grid) as typeof Grid

const useStyles = makeStyles<Theme>(() =>
  createStyles({
    button: {
      maxHeight: '56px'
    },
    buttonFullHeight: {
      height: '100%',
      '& button': {
        height: '100%'
      }
    }
  })
)

const FormNavigation = observer(
  ({ onBack, onNext, navigate, legalContact }: Props) => {
    const {
      rootStore: {
        isInIframe,
        isFooterVisible,
        urlParams: { backUrl }
      }
    } = useRootStore()

    const {
      state: { message },
      hasCooldown
    } = useErrorState()

    const { ref, inView } = useInView()

    useDebouncedEffect(
      () => {
        if (inView) {
          trackInAmplitude(AmplitudeEvent.CtaIsVisible)
        }
      },
      300,
      [inView]
    )

    const {
      page: { isFirstDisplayedPage, getCurrentIndex, scrollToFirstError },
      navigation: { currentPageInSession },
      traversal: { data },
      fieldErrors
    } = useStores()

    const loadingMode = useLoadingMode()

    const [saveButtonDialogState, setSaveButtonDialogState] =
      useState<SaveButtonDialogState>('none')
    const { isSaveButtonVisible, save } = useSaveButton({
      onSuccess: () => {
        setSaveButtonDialogState('success')
      },
      onError: () => {
        setSaveButtonDialogState('error')
      }
    })

    useEffect(() => {
      setSaveButtonDialogState('none')
    }, [data, currentPageInSession])

    const currentIndex = getCurrentIndex()

    const scrollToError = useCallback(
      () => scrollToFirstError(isInIframe),
      [isInIframe, scrollToFirstError]
    )

    const [hasErrorsOnCurrentPage, setHasErrorsOnCurrentPage] = useState(false)
    const [previousErrorPageName, setPreviousErrorPageName] = useState<string>()

    useAsyncEffect(async () => {
      if (!currentPageInSession) {
        return
      }

      const errors = await fieldErrors.getErrors(currentPageInSession)
      const hasErrors = Object.keys(errors).length > 0

      setHasErrorsOnCurrentPage(hasErrors)
      setPreviousErrorPageName(
        hasErrors
          ? await fieldErrors.findFirstErrorPage(currentIndex)
          : undefined
      )
    }, [currentPageInSession, loadingMode])

    useEffect(() => {
      if (loadingMode === LoadingMode.None && hasErrorsOnCurrentPage) {
        scrollToError()
      }
    }, [hasErrorsOnCurrentPage, loadingMode, scrollToError])

    const { button, buttonFullHeight } = useStyles()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    const isFirstPage = isFirstDisplayedPage(currentPageInSession)

    const backLabel =
      !isInIframe || currentIndex > 0 || backUrl ? 'Zurück' : undefined

    const notShowBackButton = !backLabel || isFirstPage

    if (!isFooterVisible) {
      return null
    }

    if (previousErrorPageName) {
      return (
        <PreviousPageErrors
          navigate={navigate}
          pageName={previousErrorPageName}
        />
      )
    }

    const sizeOfContinueButton = isMobile ? 12 : 8
    let sizeOfBackButton = isMobile ? 12 : 4
    const sizeOfSaveButton = isMobile ? 12 : 4

    if (isSaveButtonVisible) {
      sizeOfBackButton = 12
    }

    return (
      <CategoryLayout ref={ref}>
        {message && <HotlineFallback message={message} />}
        <SaveButtonDialog state={saveButtonDialogState} />
        <Grid
          container
          spacing={1}
          direction={isMobile || isSaveButtonVisible ? 'column' : 'row-reverse'}
          justifyContent="center"
          alignItems="center"
        >
          <FlexBasisAutoGrid
            container
            item
            spacing={1}
            direction={isMobile ? 'column' : 'row-reverse'}
            xs={12}
          >
            <Grid item xs={sizeOfContinueButton as GridSize}>
              <Button
                className={button}
                size="large"
                fullWidth
                id="next-submit"
                name="next-submit"
                variant="contained"
                color="secondary"
                endIcon={
                  loadingMode === LoadingMode.None && !isFirstPage ? (
                    <ArrowForwardIcon />
                  ) : undefined
                }
                disabled={hasCooldown}
                onClick={onNext}
                data-page={currentPageInSession}
                data-page-first={isFirstPage}
              >
                {loadingMode === LoadingMode.Blocking ? <Wave /> : 'Weiter'}
              </Button>
            </Grid>
            {isSaveButtonVisible && (
              <Grid item xs={sizeOfSaveButton as GridSize}>
                <div className={buttonFullHeight}>
                  <SaveButton onClick={save} variant="contained" />
                </div>
              </Grid>
            )}
            {!notShowBackButton && (
              <Grid item xs={sizeOfBackButton as GridSize}>
                <Button
                  className={buttonFullHeight}
                  size="large"
                  type="button"
                  id="back-submit"
                  name="back-submit"
                  onClick={onBack}
                  color="primary"
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  fullWidth
                >
                  {backLabel}
                </Button>
              </Grid>
            )}
            {currentPageInSession === 'contact' && (
              <LegalHint contact={legalContact} />
            )}
          </FlexBasisAutoGrid>
        </Grid>
      </CategoryLayout>
    )
  }
)

export default memo(FormNavigation)
